import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  isMinorBirthDate,
  computeGuestAvailableSeats,
  computeVariantAvailableSeats,
  getGuestBookableVariants,
  isFreeGuestEvent,
} from '../utils/guestEventEligibility';
import { getVariantPrice } from '../utils';
import { orderOutcome, track } from '../utils/umami';

let participantSlotCounter = 0;

const createParticipantSlotId = () => {
  participantSlotCounter += 1;
  return `participant-${participantSlotCounter}`;
};

const emptyParticipant = () => ({
  slotId: createParticipantSlotId(),
  firstName: '',
  lastName: '',
  birthDate: '',
  genitore: { nome: '', cognome: '', codiceFiscale: '' },
});

// Variante iniziale: quella cliccata se un ospite può prenotarla, oppure l'unica disponibile.
const pickInitialVariantId = (guestVariants, initialVariantId) => {
  if (guestVariants.some((v) => v.documentId === initialVariantId)) {
    return initialVariantId;
  }
  return guestVariants.length === 1 ? guestVariants[0].documentId : '';
};

export function useGuestEventBooking(event, options = {}) {
  const { isOpen, onClose, initialVariantId = null } = options;
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customer, setCustomer] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthDate: '',
  });
  const [participants, setParticipants] = useState([emptyParticipant()]);
  const [consents, setConsents] = useState({
    terminiCondizioni: false,
    autorizzazioneFotografie: false,
    consensoPubblicazione: false,
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [clientPaymentIntentId, setClientPaymentIntentId] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [socioEmailWarning, setSocioEmailWarning] = useState(false);
  const [socioEmailDismissed, setSocioEmailDismissed] = useState(false);
  const [existingBookingWarning, setExistingBookingWarning] = useState(false);

  const requiresVariant = Boolean(event?.Varianti?.length);
  const guestVariants = useMemo(() => getGuestBookableVariants(event), [event]);
  const selectedVariant =
    guestVariants.find((v) => v.documentId === selectedVariantId) || null;

  // Con varianti prezzo e posti sono quelli della variante scelta.
  const isFreeEvent = requiresVariant
    ? Boolean(selectedVariant) && getVariantPrice(selectedVariant, event) === 0
    : isFreeGuestEvent(event);
  const unitPrice = requiresVariant
    ? selectedVariant
      ? getVariantPrice(selectedVariant, event)
      : 0
    : isFreeEvent
      ? 0
      : Number(event?.prezzo) || 0;
  const totalAmount = unitPrice * quantity;
  const onlinePaymentOnly = event?.onlinePaymentOnly === true;

  const maxQty = useMemo(() => {
    if (requiresVariant) {
      const available = selectedVariant
        ? computeVariantAvailableSeats(selectedVariant)
        : 20;
      return Math.min(Math.max(1, available), 20);
    }
    const available = computeGuestAvailableSeats(event);
    const cap =
      available < 999
        ? available
        : event?.capienza != null
          ? Number(event.capienza)
          : 20;
    return Math.min(Math.max(1, cap || 1), 20);
  }, [event, requiresVariant, selectedVariant]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedVariantId(pickInitialVariantId(guestVariants, initialVariantId));
    setStep(0);
    setQuantity(1);
    setParticipants([emptyParticipant()]);
    setPaymentMethod('');
    setPaymentError('');
    setClientSecret('');
    setClientPaymentIntentId('');
    setShowPaymentForm(false);
    setSocioEmailWarning(false);
    setSocioEmailDismissed(false);
    setExistingBookingWarning(false);
    // guestVariants cambia con l'evento: basta l'evento come dipendenza
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, event?.documentId, initialVariantId]);

  useEffect(() => {
    setExistingBookingWarning(false);
  }, [customer.email, selectedVariantId]);

  // Cambiando variante i partecipanti non possono superare i posti rimasti.
  useEffect(() => {
    setQuantity((q) => Math.min(q, maxQty));
  }, [maxQty]);

  useEffect(() => {
    setParticipants((prev) => {
      const next = [];
      for (let i = 0; i < quantity; i += 1) {
        next.push(prev[i] ? { ...prev[i] } : emptyParticipant());
      }
      return next;
    });
  }, [quantity]);

  const checkCustomerEmail = useCallback(
    async (email) => {
      const emptyResult = {
        isRegisteredSocio: false,
        hasExistingBooking: false,
      };
      if (!email || !email.includes('@')) {
        setSocioEmailWarning(false);
        setExistingBookingWarning(false);
        return emptyResult;
      }
      try {
        const res = await axios.get('/api/public/eventi/check-email', {
          params: {
            email: email.trim(),
            urlTitle: event?.urlTitle,
            ...(selectedVariantId ? { variantId: selectedVariantId } : {}),
          },
        });
        const result = {
          isRegisteredSocio: Boolean(res.data?.isRegisteredSocio),
          hasExistingBooking: Boolean(res.data?.hasExistingBooking),
        };
        setSocioEmailWarning(result.isRegisteredSocio);
        setExistingBookingWarning(result.hasExistingBooking);
        if (result.hasExistingBooking) setPaymentError('');
        if (!result.isRegisteredSocio) setSocioEmailDismissed(false);
        return result;
      } catch {
        setSocioEmailWarning(false);
        setExistingBookingWarning(false);
        return emptyResult;
      }
    },
    [event?.urlTitle, selectedVariantId]
  );

  const validateVariant = () => {
    if (!requiresVariant) return null;
    if (!guestVariants.length) {
      return 'Nessuna variante è prenotabile come ospite';
    }
    return selectedVariant ? null : 'Scegli a quale variante partecipare';
  };

  const validateParticipants = () => {
    for (let i = 0; i < participants.length; i += 1) {
      const p = participants[i];
      if (!p.firstName?.trim() || !p.lastName?.trim()) {
        return `Nome e cognome obbligatori per il partecipante ${i + 1}`;
      }
      if (!p.birthDate) {
        return `Data di nascita obbligatoria per il partecipante ${i + 1}`;
      }
      if (isMinorBirthDate(p.birthDate)) {
        const g = p.genitore || {};
        if (!g.nome?.trim() || !g.cognome?.trim() || !g.codiceFiscale?.trim()) {
          return `Dati genitore obbligatori per il partecipante minorenne ${i + 1}`;
        }
      }
    }
    return null;
  };

  const validateCustomer = () => {
    if (!customer.firstName?.trim()) return 'Nome acquirente obbligatorio';
    if (!customer.lastName?.trim()) return 'Cognome acquirente obbligatorio';
    if (!customer.email?.trim()) return 'Email obbligatoria';
    if (!customer.phone?.trim()) return 'Telefono obbligatorio';
    if (!customer.birthDate) return 'Data di nascita acquirente obbligatoria';
    return null;
  };

  const validateConsents = () => {
    if (!consents.terminiCondizioni)
      return 'Devi accettare i termini e condizioni';
    return null;
  };

  const buildOrderBody = (overrides = {}) => ({
    customer: {
      firstName: customer.firstName.trim(),
      lastName: customer.lastName.trim(),
      email: customer.email.trim(),
      phone: customer.phone.trim(),
      birthDate: customer.birthDate,
    },
    participants: participants.map((p) => ({
      firstName: p.firstName.trim(),
      lastName: p.lastName.trim(),
      birthDate: p.birthDate,
      ...(isMinorBirthDate(p.birthDate)
        ? {
            genitore: {
              nome: p.genitore?.nome?.trim() || '',
              cognome: p.genitore?.cognome?.trim() || '',
              codiceFiscale: p.genitore?.codiceFiscale?.trim() || '',
            },
          }
        : {}),
    })),
    consents,
    amount: totalAmount,
    ...(selectedVariant ? { variantId: selectedVariant.documentId } : {}),
    ...overrides,
  });

  const createPaymentIntent = async () => {
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const response = await axios.post('/api/create-payment-intent', {
        currency: 'eur',
        eventUrlTitle: event.urlTitle,
        eventId: event.documentId,
        amount: totalAmount * 100,
        guestBooking: true,
        customerEmail: customer.email.trim(),
        guestParticipantCount: quantity,
        ...(selectedVariant
          ? { guestVariantId: selectedVariant.documentId }
          : {}),
        eventVariants: selectedVariant
          ? {
              [selectedVariant.documentId]: {
                type: 'variant',
                priceOption: 'totale',
                amount: unitPrice,
                name: selectedVariant.Nome,
              },
            }
          : {
              [event.documentId]: {
                type: 'event',
                priceOption: 'totale',
                amount: event.prezzo,
                name: event.Nome,
              },
            },
      });
      setClientSecret(response.data.clientSecret);
      setClientPaymentIntentId(response.data.localPaymentIntentId);
      setShowPaymentForm(true);
    } catch (err) {
      setPaymentError(
        err?.response?.data?.error || err?.message || 'Errore pagamento'
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const goToSuccess = ({
    orderId,
    paymentMethod: pm,
    paymentStatus: ps,
  } = {}) => {
    navigate(
      `/event/${encodeURIComponent(event.urlTitle)}/prenotazione-confermata`,
      {
        state: {
          email: customer.email.trim(),
          totalAmount,
          paymentMethod: pm,
          paymentStatus: ps,
          eventName: event.Nome,
          variantName: selectedVariant?.Nome || null,
          quantity,
          orderId,
        },
      }
    );
    if (typeof onClose === 'function') onClose();
  };

  const trackedBooking = () => ({
    event_id: event?.documentId,
    variant_id: selectedVariant?.documentId,
    flow: 'ospite',
    partecipanti: quantity,
  });

  const submitOrder = async (overrides = {}) => {
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const body = buildOrderBody(overrides);
      const res = await axios.post(
        `/api/public/eventi/${encodeURIComponent(event.urlTitle)}/ordini`,
        body
      );
      const order = res.data?.order?.data || res.data?.order;
      track.bookingComplete({
        ...trackedBooking(),
        ...orderOutcome({
          paymentMethod: body.paymentMethod,
          paymentStatus: body.paymentStatus,
          amount: body.amount,
        }),
      });
      goToSuccess({
        orderId: order?.documentId,
        paymentMethod: body.paymentMethod,
        paymentStatus: body.paymentStatus,
      });
    } catch (err) {
      if (
        err?.response?.status === 409 &&
        overrides.paymentStatus === 'succeeded'
      ) {
        goToSuccess({
          orderId: err?.response?.data?.existingOrderId,
          paymentMethod: overrides.paymentMethod || 'elettronico',
          paymentStatus: overrides.paymentStatus || 'succeeded',
        });
        return;
      }
      setPaymentError(
        err?.response?.data?.error ||
          err?.message ||
          'Errore nella creazione della prenotazione'
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleConfirmFree = async () => {
    if (customer.email?.includes('@')) {
      const emailCheck = await checkCustomerEmail(customer.email);
      if (emailCheck.hasExistingBooking) {
        return;
      }
    }
    const err =
      validateVariant() ||
      validateCustomer() ||
      validateParticipants() ||
      validateConsents();
    if (err) {
      setPaymentError(err);
      return;
    }
    track.checkoutStart({ ...trackedBooking(), metodo: 'gratuito' });
    await submitOrder({
      paymentMethod: 'contanti',
      paymentStatus: 'succeeded',
      amount: 0,
    });
  };

  const handleConfirmCash = async () => {
    if (customer.email?.includes('@')) {
      const emailCheck = await checkCustomerEmail(customer.email);
      if (emailCheck.hasExistingBooking) {
        return;
      }
    }
    const err =
      validateVariant() ||
      validateCustomer() ||
      validateParticipants() ||
      validateConsents();
    if (err) {
      setPaymentError(err);
      return;
    }
    track.checkoutStart({ ...trackedBooking(), metodo: 'contanti' });
    await submitOrder({ paymentMethod: 'contanti', paymentStatus: 'pending' });
  };

  const handleProceedToStripe = async () => {
    if (customer.email?.includes('@')) {
      const emailCheck = await checkCustomerEmail(customer.email);
      if (emailCheck.hasExistingBooking) {
        return;
      }
    }
    const err =
      validateVariant() ||
      validateCustomer() ||
      validateParticipants() ||
      validateConsents();
    if (err) {
      setPaymentError(err);
      return;
    }
    track.checkoutStart({ ...trackedBooking(), metodo: 'stripe' });
    await createPaymentIntent();
  };

  const onPaymentSuccess = async ({
    paymentIntentId,
    paymentMethodId,
    paymentStatus,
  }) => {
    await submitOrder({
      paymentMethod: 'elettronico',
      paymentIntentId,
      paymentMethodId,
      paymentIntents: clientPaymentIntentId,
      paymentStatus: paymentStatus || 'succeeded',
    });
  };

  const updateParticipant = (index, field, value) => {
    setParticipants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateParticipantGenitore = (index, field, value) => {
    setParticipants((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        genitore: { ...(next[index].genitore || {}), [field]: value },
      };
      return next;
    });
  };

  return {
    step,
    setStep,
    requiresVariant,
    guestVariants,
    selectedVariant,
    setSelectedVariantId,
    quantity,
    setQuantity,
    maxQty,
    customer,
    setCustomer,
    participants,
    updateParticipant,
    updateParticipantGenitore,
    consents,
    setConsents,
    paymentMethod,
    setPaymentMethod,
    paymentError,
    setPaymentError,
    paymentLoading,
    clientSecret,
    showPaymentForm,
    totalAmount,
    unitPrice,
    isFreeEvent,
    onlinePaymentOnly,
    socioEmailWarning,
    socioEmailDismissed,
    setSocioEmailDismissed,
    existingBookingWarning,
    checkCustomerEmail,
    validateVariant,
    validateCustomer,
    validateParticipants,
    validateConsents,
    handleConfirmFree,
    handleConfirmCash,
    handleProceedToStripe,
    onPaymentSuccess,
  };
}
